import { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { TablePagination } from '@/components/ui/TablePagination';
import { paginateClientList, usePagination } from '@/hooks/usePagination';
import { financeApi } from '@/api/finance.api';
import { showApiError } from '@/utils/apiError';
import type { FinAccountsPayable, FinPaymentRun } from '@/types/finance';
import { notify } from '@/core/toast/notify';

export function FinanceApPage() {
  const payablesPagination = usePagination();
  const runsPagination = usePagination();
  const [payables, setPayables] = useState<FinAccountsPayable[]>([]);
  const [runs, setRuns] = useState<FinPaymentRun[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ap, pr] = await Promise.all([financeApi.listOpenPayables(), financeApi.listPaymentRuns()]);
      setPayables(ap);
      setRuns(pr);
      payablesPagination.applyClientCount(ap.length);
      runsPagination.applyClientCount(pr.length);
    } catch (e) {
      showApiError(e, 'Failed to load AP data');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- client-side pagination; fetch on mount/actions only
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const createRun = async () => {
    try {
      await financeApi.createPaymentRun();
      notify.success('Payment run created from open payables');
      void load();
    } catch (e) {
      showApiError(e, 'Create payment run failed');
    }
  };

  const approveRun = async (id: number) => {
    try {
      await financeApi.approvePaymentRun(id);
      notify.success('Payment run approved');
      void load();
    } catch (e) {
      showApiError(e, 'Approve failed');
    }
  };

  const postRun = async (id: number) => {
    try {
      await financeApi.postPaymentRun(id);
      notify.success('Payment run posted to GL');
      void load();
    } catch (e) {
      showApiError(e, 'Post failed');
    }
  };

  const payablesPage = paginateClientList(payables, payablesPagination.page, payablesPagination.size);
  const runsPage = paginateClientList(runs, runsPagination.page, runsPagination.size);

  return (
    <div>
      <PageHeader
        title="Accounts Payable"
        description="Open vendor obligations, payment runs, and batch disbursements"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Finance', to: '/finance' }, { label: 'Payables' }]}
        actions={<Button onClick={() => void createRun()}>Create payment run</Button>}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="card overflow-x-auto">
          <h3 className="font-semibold p-3 border-b">Open payables</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="p-2 text-left">Vendor</th>
                <th className="p-2 text-left">Source</th>
                <th className="p-2 text-right">Open</th>
                <th className="p-2 text-left">Due</th>
              </tr>
            </thead>
            <tbody>
              {payablesPage.map((ap) => (
                <tr key={ap.id} className="border-b">
                  <td className="p-2">#{ap.vendorId}</td>
                  <td className="p-2">
                    {ap.sourceReferenceType ?? '—'}
                    {ap.sourceReferenceId != null ? ` #${ap.sourceReferenceId}` : ''}
                  </td>
                  <td className="p-2 text-right font-mono">{ap.openAmount?.toFixed(2)}</td>
                  <td className="p-2">{ap.dueDate ?? '—'}</td>
                </tr>
              ))}
              {payablesPage.length === 0 && !loading && (
                <tr>
                  <td colSpan={4} className="p-4 text-center text-slate-500">
                    No open payables
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <TablePagination
            page={payablesPagination.page}
            totalPages={payablesPagination.totalPages}
            totalElements={payablesPagination.totalElements}
            onPageChange={payablesPagination.setPage}
            loading={loading}
            hasRows={payables.length > 0}
          />
        </div>
        <div className="card overflow-x-auto">
          <h3 className="font-semibold p-3 border-b">Payment runs</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="p-2 text-left">Run</th>
                <th className="p-2 text-left">Date</th>
                <th className="p-2 text-right">Total</th>
                <th className="p-2 text-left">Status</th>
                <th className="p-2" />
              </tr>
            </thead>
            <tbody>
              {runsPage.map((run) => (
                <tr key={run.id} className="border-b">
                  <td className="p-2 font-mono">{run.runNumber}</td>
                  <td className="p-2">{run.runDate}</td>
                  <td className="p-2 text-right font-mono">{run.totalAmount?.toFixed(2)}</td>
                  <td className="p-2">{run.statusCode}</td>
                  <td className="p-2 text-right space-x-1">
                    {run.statusCode === 'DRAFT' && (
                      <Button variant="secondary" onClick={() => void approveRun(run.id)}>
                        Approve
                      </Button>
                    )}
                    {run.statusCode === 'APPROVED' && (
                      <Button variant="secondary" onClick={() => void postRun(run.id)}>
                        Post
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {runsPage.length === 0 && !loading && (
                <tr>
                  <td colSpan={5} className="p-4 text-center text-slate-500">
                    No payment runs yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <TablePagination
            page={runsPagination.page}
            totalPages={runsPagination.totalPages}
            totalElements={runsPagination.totalElements}
            onPageChange={runsPagination.setPage}
            loading={loading}
            hasRows={runs.length > 0}
          />
        </div>
      </div>
    </div>
  );
}
